import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import * as analytics from '../lib/analytics';
import { useAnalyticsContext } from '../contexts/AnalyticsContext';

/**
 * Custom hook for analytics tracking
 * Provides access to all analytics tracking functions
 */
export const useAnalytics = () => {
  const { isInitialized } = useAnalyticsContext();

  return {
    isInitialized,
    ...analytics,
  };
};

/**
 * Hook to automatically track page views
 */
export const usePageTracking = (pageName: string, additionalData?: Record<string, any>) => {
  const location = useLocation();
  const { isInitialized, trackPageView } = useAnalytics();
  const hasTracked = useRef(false);

  useEffect(() => {
    if (isInitialized && !hasTracked.current) {
      trackPageView(pageName, additionalData);
      hasTracked.current = true;
    }
  }, [isInitialized, pageName, trackPageView, additionalData]);
};

/**
 * Hook to track time spent on a step/page
 */
export const useStepTimer = () => {
  const startTimeRef = useRef<number>(Date.now());

  const getTimeSpent = () => {
    return Math.floor((Date.now() - startTimeRef.current) / 1000); // in seconds
  };

  const resetTimer = () => {
    startTimeRef.current = Date.now();
  };

  return { getTimeSpent, resetTimer };
};

/**
 * Hook to track wizard step progress
 */
export const useWizardStepTracking = (stepName: string, stepNumber: number, totalSteps: number) => {
  const { trackWizardStepView } = useAnalytics();
  const hasTrackedView = useRef(false);

  useEffect(() => {
    if (!hasTrackedView.current) {
      const progressPercent = Math.round((stepNumber / totalSteps) * 100);
      trackWizardStepView({
        stepName,
        stepNumber,
        progressPercent,
      });
      hasTrackedView.current = true;
    }
  }, [stepName, stepNumber, totalSteps, trackWizardStepView]);
};

/**
 * Hook to track modal open/close
 */
export const useModalTracking = (modalName: string) => {
  const { trackModalOpen, trackModalClose } = useAnalytics();
  const openTimeRef = useRef<number | null>(null);

  const onOpen = (triggerSource: string) => {
    openTimeRef.current = Date.now();
    trackModalOpen(modalName, triggerSource);
  };

  const onClose = (closeMethod: string = 'button') => {
    if (openTimeRef.current) {
      const timeViewed = Math.floor((Date.now() - openTimeRef.current) / 1000);
      trackModalClose({
        modalName,
        timeViewed,
        closeMethod,
      });
      openTimeRef.current = null;
    }
  };

  return { onOpen, onClose };
};

/**
 * Hook to track form interactions
 */
export const useFormTracking = (formName: string, pageLocation: string) => {
  const { trackFormStart, trackFormSubmit, trackFormFieldComplete } = useAnalytics();
  const hasStarted = useRef(false);

  const onFormStart = () => {
    if (!hasStarted.current) {
      trackFormStart(formName, pageLocation);
      hasStarted.current = true;
    }
  };

  const onFieldComplete = (fieldName: string) => {
    trackFormFieldComplete(formName, fieldName);
  };

  const onFormSubmit = (success: boolean, errorMessage?: string) => {
    trackFormSubmit({
      formName,
      success,
      errorMessage,
    });
  };

  return {
    onFormStart,
    onFieldComplete,
    onFormSubmit,
  };
};

export default useAnalytics;
